<template>
    <div>
        <!--Edit File Dialog-->
        <Dialog :visible.sync="fileDialog" :style="{width: '750px'}" header="Files" :modal="true" :maximizable="true">
            <FileUpload :disabled="loading" :maxFileSize="3000000"
                        accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf"
                        :customUpload="true" @uploader="customUpload" style="margin-bottom: 5em;">
                <template #empty>
                    <p>Drag and drop files to here to upload.</p>
                </template>
            </FileUpload>
            <DataTable :value="files" v-if="files.length" :loading="loading">
                <Column field="name" header="Name"></Column>
                <Column field="created-at" header="Uploaded">
                    <template #body="slotProps">
                        {{ $moment(slotProps.data['created-at']) }}
                    </template>
                </Column>
                <Column>
                    <template #body="slotProps">
                        <Button icon="pi pi-download" class="p-button-rounded p-button-info p-mr-2"
                                @click="download(slotProps.data)"/>
                        <Button icon="pi pi-trash" class="p-button-rounded p-button-warning p-mr-2"
                                @click="confirmDeleteFile(slotProps.data)"/>
                    </template>
                </Column>
                <template #empty>
                    No records found.
                </template>
            </DataTable>
            <p v-else style="margin-bottom: 5em;"> No records found. Please upload some files.</p>
        </Dialog>

        <!--Delete File Dialog-->
        <Dialog :visible.sync="deleteFileDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="file">Are you sure you want to delete <b>{{ file.name }}</b>?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="deleteFileDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text" @click="deleteFile"/>
            </template>
        </Dialog>
    </div>
</template>

<script>
import {Component, Prop, Vue, Watch} from "vue-property-decorator";

import Toast from 'primevue/toast';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Dialog from "primevue/dialog";
import Dropdown from 'primevue/dropdown';
import FileUpload from 'primevue/fileupload';
import SelectButton from 'primevue/selectbutton';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import AutoComplete from 'primevue/autocomplete';
import InputSwitch from 'primevue/inputswitch';
import store from "../../store";

/**
 * Class-component
 */
@Component({
    components: {
        Toast: Vue.extend(Toast),
        Column: Vue.extend(Column),
        DataTable: Vue.extend(DataTable),
        Dialog: Vue.extend(Dialog),
        Dropdown: Vue.extend(Dropdown),
        FileUpload: Vue.extend(FileUpload),
        SelectButton: Vue.extend(SelectButton),
        InputText: Vue.extend(InputText),
        Button: Vue.extend(Button),
        AutoComplete: Vue.extend(AutoComplete),
        InputSwitch: Vue.extend(InputSwitch),
    }
})
export default class FileDialog extends Vue {
    fileDialog = false;
    deleteFileDialog = false;
    record = {};
    files = [];
    file = {};
    loading = false;
    toastSuccess = {
        severity: 'success',
        summary: 'Successful',
        detail: '',
        life: 3000
    };
    toastError = {
        severity: 'error',
        summary: 'Error',
        detail: '',
        life: 3000
    };

    async customUpload(event) {
        function _arrayBufferToBase64(buffer) {
            let binary = '';
            const bytes = new Uint8Array(buffer);
            const len = bytes.byteLength;
            for (let i = 0; i < len; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            return window.btoa(binary);
        }

        const relObj = {};
        if (this.record._jv.type === 'academics') relObj['academic'] = {
            data: {
                id: this.record._jv.id,
                type: 'academics'
            }
        }
        if (this.record._jv.type === 'staff-professionals') relObj['professional'] = {
            data: {
                id: this.record._jv.id,
                type: 'staff-professionals'
            }
        }
        if (this.record._jv.type === 'trainings') relObj['training'] = {
            data: {
                id: this.record._jv.id,
                type: 'trainings'
            }
        }
        if (this.record._jv.type === 'documents') relObj['document'] = {
            data: {
                id: this.record._jv.id,
                type: 'documents'
            }
        }

        const fileObj = {
            _jv: {
                type: 'files',
                relationships: relObj
            }
        };

        try {
            this.loading = true;
            for (const file of event.files) {
                // const buffer = await file.arrayBuffer();
                const buffer = await new Response(file).arrayBuffer();
                fileObj['name'] = file.name;
                fileObj['mime'] = file.type;
                fileObj['base64'] = _arrayBufferToBase64(buffer);
                await store.dispatch('jv/post', fileObj);
            }
            this.record = await store.dispatch('jv/get', [`${this.record._jv.type}/${this.record._jv.id}`, {
                params: {
                    include: 'files',
                    'fields[files]': 'name,created-at'
                }
            }]);
            this.files = Object.values(this.record.files);
            this.toastSuccess.detail = 'Files Uploaded'
            this.$toast.add(this.toastSuccess);
            this.loading = false;
        } catch (e) {
            this.toastError.detail = e.message
            this.toastError.life = null
            this.$toast.add(this.toastError);
            this.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                    life: null
                });
            })
        }
    }

    async download(file) {
        this.loading = true;
        file = await store.dispatch('jv/get', `${file._jv.type}/${file._jv.id}`);

        function _base64ToArrayBuffer(base64) {
            const binary = window.atob(base64);
            const len = binary.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
                bytes[i] = binary.charCodeAt(i);
            }
            return bytes.buffer;
        }

        const blob = new Blob([_base64ToArrayBuffer(file.base64)], {
            type: file.mime
        });

        const link = document.createElement("a");
        link.style.display = 'none';
        document.body.appendChild(link);
        if (link.download !== undefined) {
            link.setAttribute('href', URL.createObjectURL(blob));
            link.setAttribute('download', file.name);
            link.click();
        }
        document.body.removeChild(link);
        this.toastSuccess.detail = 'File Downloaded'
        this.$toast.add(this.toastSuccess);
        this.loading = false;
    }

    confirmDeleteFile(file) {
        this.deleteFileDialog = true;
        this.file = file;
    }

    async deleteFile() {
        try {
            this.loading = true;
            await store.dispatch('jv/delete', `files/${this.file._jv.id}`)
            this.record = await store.dispatch('jv/get', [`${this.record._jv.type}/${this.record._jv.id}`, {
                params: {
                    include: 'files',
                    'fields[files]': 'name,created-at'
                }
            }]);
            this.files = Object.values(this.record.files);
            this.toastSuccess.detail = 'File Deleted'
            this.$toast.add(this.toastSuccess);
            this.loading = false;
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                    life: null
                });
            })
        }
        this.deleteFileDialog = false;
        this.file = {};
    }

    logError(e) {
        const error = []
        if (e.response.data.errors) {
            // Work with each error from the JSONAPI 'errors' array
            for (const err of e.response.data.errors) {
                error.push(err.detail)
            }
        }
        return error
    }
}


</script>

<style scoped>

</style>